import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Sparkles, Pencil } from "lucide-react";
import { formatDate } from "@/lib/date";
import { CATEGORY_EMOJI } from "@/lib/reportVaultConstants";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

// A single report row in the vault list — filename/category header, action
// buttons (View, Analyze/Regenerate, toggle AI analysis) and, when expanded,
// the AI extraction summary + editable parameter rows.
export function ReportListItem({ report: r, analysis, isProcessing, expanded, setExpandedReportId, isDoctor, analyzingId, runAnalyze, viewReport, saveParamEdit }) {
  const isReadOnly = useIsGracePeriod();
  const isAnalyzable = ["lab_report", "previous_report"].includes(r.category);

  return (
    <div className="card-surface card-surface-hover rounded-lg border border-slate-200" data-testid={`report-row-${r.id}`}>
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-900" data-testid={`report-title-${r.id}`}>
              {r.category_detected && <span className="mr-1">{CATEGORY_EMOJI[r.category_detected]}</span>}
              {r.display_name || r.stored_filename}
            </p>
            <p className="text-xs text-slate-500 capitalize">{r.category.replace("_", " ")} · uploaded by {r.uploaded_by}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isProcessing && (
            <span className="flex items-center gap-1 text-xs text-teal-700" data-testid={`analyzing-badge-${r.id}`}>
              <Sparkles className="h-3 w-3 animate-pulse" /> AI analyzing in background...
            </span>
          )}
          {analysis && isDoctor && (
            <button
              data-testid={`toggle-analysis-${r.id}`}
              onClick={() => setExpandedReportId(expanded ? null : r.id)}
              className="flex items-center gap-1 text-xs text-slate-600 hover:underline"
            >
              <Sparkles className="h-3 w-3 text-teal-700" /> {expanded ? "Hide AI Analysis" : "View AI Analysis"}
            </button>
          )}
          {isAnalyzable && isDoctor && !isProcessing && (
            <button
              data-testid={`analyze-report-${r.id}`}
              disabled={analyzingId === r.id || isReadOnly}
              title={isReadOnly ? "Disabled during subscription grace period" : undefined}
              onClick={() => runAnalyze(r.id)}
              className="text-xs text-teal-700 hover:underline disabled:opacity-50"
            >
              {analyzingId === r.id ? "Analyzing..." : analysis ? "Regenerate Analysis" : "Analyze with AI"}
            </button>
          )}
          <button data-testid={`view-report-${r.id}`} onClick={() => viewReport(r.id)} className="text-xs text-teal-700 hover:underline">
            View
          </button>
        </div>
      </div>
      {expanded && analysis && (
        <div className="space-y-2 border-t border-slate-100 bg-slate-50/60 p-4">
          <p className="text-xs text-slate-400">Last generated {formatDate(analysis.analyzed_at)}</p>
          <p className="text-xs italic text-slate-500">{analysis.extraction_summary}</p>
          {analysis.report_snapshot?.length > 0 && (
            <div className="rounded-md border border-teal-200 bg-teal-50/60 p-3" data-testid={`report-snapshot-${r.id}`}>
              <p className="flex items-center gap-1 text-xs font-semibold uppercase text-teal-800">
                <Sparkles className="h-3 w-3" /> Quick Physician Snapshot
              </p>
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-teal-900">
                {analysis.report_snapshot.map((s, i) => (
                  <li key={`${i}-${s}`}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {analysis.parameters.length === 0 && (
            <p className="text-sm text-slate-400" data-testid={`no-parameters-${r.id}`}>No extractable test values were found in this document.</p>
          )}
          {analysis.parameters.map((p) => (
            <ParameterRow key={p.parameter} reportId={r.id} param={p} isDoctor={isDoctor} onSave={saveParamEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

function ParameterRow({ reportId, param, isDoctor, onSave }) {
  const isReadOnly = useIsGracePeriod();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(param.value || "");
  const confidenceColor = { HIGH: "bg-green-100 text-green-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-red-100 text-red-700" }[param.confidence_label] || "bg-slate-100 text-slate-600";

  if (param.status !== "extracted" && !param.manually_edited) {
    return (
      <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={`param-${reportId}-${param.parameter}`}>
        <span className="text-sm text-slate-700">{param.parameter}</span>
        <span className="text-xs italic text-slate-400">Not Tested — Pending</span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={`param-${reportId}-${param.parameter}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-900">{param.parameter}</span>
        {param.manually_edited ? (
          <span className="rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">Manually Verified — {param.verified_by}</span>
        ) : (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${confidenceColor}`}>{param.confidence_label} · {param.confidence_percent}% Confidence</span>
        )}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-2">
          <Input data-testid={`edit-param-input-${reportId}-${param.parameter}`} value={value} onChange={(e) => setValue(e.target.value)} className="h-8 text-sm" placeholder="Value only, unit shown separately" />
          {param.unit && <span className="text-xs text-slate-500">{param.unit}</span>}
          <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800" onClick={() => { onSave(reportId, param.parameter, value); setEditing(false); }}>
            Save
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="mt-1 flex items-center justify-between">
          <p className="text-sm text-slate-700">
            {param.unit && String(param.value).trim().toLowerCase().endsWith(param.unit.toLowerCase())
              ? param.value
              : `${param.value} ${param.unit || ""}`}
            {param.normal_range && (
              <span className="ml-2 text-xs font-normal text-slate-400" data-testid={`param-normal-range-${reportId}-${param.parameter}`}>
                (Normal: {param.normal_range})
              </span>
            )}
          </p>
          {isDoctor && (
            <button data-testid={`edit-param-btn-${reportId}-${param.parameter}`} disabled={isReadOnly} title={isReadOnly ? "Disabled during subscription grace period" : undefined} onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-teal-700 hover:underline disabled:opacity-50">
              <Pencil className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
      )}
      {param.manually_edited && param.original_ai_value && (
        <p className="mt-0.5 text-xs italic text-slate-400">Original AI value: {param.original_ai_value} ({param.original_ai_confidence_percent}%)</p>
      )}
    </div>
  );
}
